import { Container, debounce, Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import Image from "next/image";
import { useRouter } from "next/router";
import React, { ChangeEvent, useState } from "react";
import { CircleSpinner } from "react-spinners-kit";
import { useTheme } from "styled-components";

import { AudienceContentItemService } from "@/api/services/audience-content-item.service";
import { ForumService } from "@/api/services/forum.service";
import Button from "@/components/buttons/Button";
import TagList from "@/components/buttons/TagList/TagList";
import ForumPostWrapper from "@/components/content-types/ForumPost/ForumPostWrapper";
import Input from "@/components/form/Input/Input";
import SortBar from "@/components/form/SortBar/SortBar";
import ChevronRight from "@/components/icons/ChevronRight/ChevronRight";
import SearchIcon from "@/components/icons/SearchIcon/SearchIcon";
import { Hero, Pagination } from "@/components/layout";
import PageWrapper from "@/components/layout/PageWrapper/PageWrapper";
import { H4, P, TitleWithHighlights } from "@/components/typography";
import { FORUM_POST_PER_PAGE } from "@/constants/app-configs";
import { AudienceName } from "@/enums/audienceName";
import { ContentItemType } from "@/enums/contentItemType";
import { HeroButtonWrapper } from "@/styles/kinderen/index.styles";
import { COLORS } from "@/styles/theme";
import {
  ContentItemSortingOption,
  ForumOverview,
} from "@/types/api/audience-content-item.types";
import { ForumPageProps } from "@/types/pageTypes";
import { mapToForumPost } from "@/utils/MapTypes";
import parseImageURL2 from "@/utils/parseImageURL2";

const forumSortOptions: { name: string; value: ContentItemSortingOption }[] = [
  { name: "Titel (a-z)", value: "Title" },
  { name: "Titel (z-a)", value: "Title desc" },
  { name: "Auteur (a-z)", value: "AuthorName" },
  { name: "Auteur (z-a)", value: "AuthorName desc" },
  { name: "Datum (oud-nieuw)", value: "CreationTime" },
  { name: "Datum (nieuw-oud)", value: "CreationTime desc" },
];

export const getServerSideProps = async () => {
  const pageData = await ForumService.getForumOverviewPageData(
    AudienceName.Children,
  );
  return {
    props: {
      pageData,
    },
  };
};

export default function Forum({
  pageData,
  audienceName = AudienceName.Children,
}: ForumPageProps) {
  const theme = useTheme();
  const [selectedTag, setSelectedTag] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [sort, setSort] =
    useState<ContentItemSortingOption>("CreationTime desc");

  const router = useRouter();
  const { maxCommentCount } = router.query;

  const { data: result, isFetching } = useQuery({
    queryKey: [
      "forum",
      currentPage,
      search,
      sort,
      selectedTag,
      maxCommentCount,
    ],
    queryFn: async ({ signal }) => {
      const data =
        await AudienceContentItemService.getPagedPublishedContentItems(
          {
            ContentTypes: [ContentItemType.Forum],
            AudienceName: audienceName,
            ThemeId: selectedTag,
            Sorting: sort,
            SkipCount: currentPage * FORUM_POST_PER_PAGE - FORUM_POST_PER_PAGE,
            Filter: search,
            MaxResultCount: FORUM_POST_PER_PAGE,
          },
          signal,
        );
      setCurrentPage(currentPage);
      return data;
    },
    initialData: {
      totalCount: 0,
      items: [],
    },
  });

  const changePage = (index: number) => {
    if (index <= 1) {
      setCurrentPage(1);
    } else {
      setCurrentPage(index);
    }
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(e.target.value);
    debounce(() => {
      setCurrentPage(1);
      setSearch(e.target.value);
    }, 300)();
  };

  const handleSort = (x: string) => {
    setSort(x as ContentItemSortingOption);
  };

  return (
    <PageWrapper
      seo={{
        title: pageData?.seoTitle ? pageData?.seoTitle : pageData?.pageTitle,
        description: pageData?.seoDescription
          ? pageData?.seoDescription
          : pageData?.pageSubtitle,
        image: pageData?.seoImageId ? parseImageURL2(pageData?.seoImageId) : "",
      }}
    >
      <Hero
        center
        imageUrl={parseImageURL2(pageData?.headerImageId, 1400)}
        style={{
          minHeight: 649,
          position: "relative",
        }}
        darkenImage={pageData?.hasDarkOverlayHeaderImage}
        darkenImageMobile={pageData?.hasDarkOverlayHeaderImage}
        mobileImageHeight={649}
      >
        <Container className="mt-[40px] max-w-[1384px] md:mt-0">
          <Grid container>
            <Grid item xs={12} md={8} className="mx-auto w-[100%]">
              <TitleWithHighlights
                text={pageData?.pageTitle ?? ""}
                color="white"
                className={
                  "mt-[-80px] text-[46px] font-[400] sm:text-[46px] md:mt-0 md:text-center md:text-[64px]"
                }
              />
              <P
                color="white"
                variant="light"
                className={
                  "text-subtitleHeaderMobileSize md:text-subtitleHeaderSize text-center leading-[160%] font-[300]"
                }
                style={{
                  margin: "0 auto",
                }}
              >
                {pageData?.pageSubtitle}
              </P>

              <HeroButtonWrapper className="mx-auto mt-[40px] w-fit gap-5 md:flex">
                <Button
                  href={pageData?.questionSubmitButtonLink}
                  variant={"headerMain"}
                  className="forum-act mb-[16px] w-[300px] px-[5px] text-[18px] font-[400] text-white"
                >
                  {pageData?.questionSubmitButtonText}
                </Button>
                <Button
                  variant="infoReversed"
                  href={pageData?.questionOthersButtonLink}
                  className="forum-act hover:bg-teal w-[300px] border border-white bg-[transparent] px-[5px] text-[18px] font-[400] text-white hover:border-none"
                >
                  {pageData?.questionOthersButtonText}
                </Button>
              </HeroButtonWrapper>
            </Grid>
          </Grid>
        </Container>
      </Hero>

      <main style={{ marginBottom: "80px" }}>
        <div
          style={{
            marginBottom: "20px",
            transform: "translateY(-50%)",
          }}
        >
          <Container className="max-w-[1384px]">
            <TagList
              tags={pageData?.themes ?? []}
              selected={selectedTag}
              prefix={
                <H4
                  style={{
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "5px",
                  }}
                  className="forum-tag"
                >
                  Onderwerp 👉
                </H4>
              }
              suffix={<ChevronRight />}
              onSelect={(x: string) => {
                setSelectedTag(x);
                setCurrentPage(1);
              }}
            />
          </Container>
        </div>
        <Container
          className="mx-[auto] mt-[-90px] max-w-[1384px] px-[32px] md:mt-[0px] md:mb-[56px]"
          id={"posts"}
        >
          <Grid container spacing={"34px"}>
            <>
              <Grid item xs={12} md={9}>
                <P hidden={isFetching} className={"text-[24px]"}>
                  {result?.totalCount}{" "}
                  {result?.totalCount === 1 ? "vraag" : "vragen"}
                </P>
              </Grid>
              <Grid item xs={12} md={3}>
                <SortBar sortOptions={forumSortOptions} onSort={handleSort} />
              </Grid>
              <Grid item xs={12} md={8}>
                <div className="flex h-[100%] flex-col">
                  <Image
                    src={"/forumbothead.png"}
                    alt="forum search"
                    fill
                    className="relative hidden max-h-[250px] w-[100%] rounded-t-[8px] object-cover md:block"
                  />
                  <div className="bg-pink rounded-lg p-[24px] md:h-[290px] md:rounded-t-none md:p-[32px]">
                    <div>
                      <h3
                        className={"mb-4 hyphens-auto text-white md:mb-8"}
                        lang={"nl"}
                      >
                        {pageData?.searchBarQuote}
                      </h3>
                      <div className="md:flex md:items-center">
                        <div className={`mt-[20px] flex-1 md:mt-[0px]`}>
                          <Input
                            iconLeft={
                              <SearchIcon color={COLORS.white.normal} />
                            }
                            value={searchInputValue}
                            placeholder={"Zoek in het Forum..."}
                            onChange={handleSearch}
                            borderColor="white"
                            className={`[&_.input-field]:bg-pink [&_input]:text-white [&_input]:placeholder:text-white [&_input]:placeholder:opacity-40`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Grid>
              {isFetching && (
                <Grid
                  item
                  xs={12}
                  md={4}
                  className={"flex items-center justify-center"}
                >
                  <CircleSpinner
                    size={75}
                    color={theme.colors.primary.normal}
                  />
                </Grid>
              )}
              {!isFetching && result.items && result.items.length === 0 && (
                <Grid
                  item
                  xs={12}
                  md={4}
                  className={
                    "flex flex-col items-center justify-center gap-5 text-center"
                  }
                >
                  <P>
                    Er zijn helaas geen vragen gevonden
                    {selectedTag && (
                      <>
                        {" met het onderwerp "}
                        <span className={"font-bold"}>
                          {pageData?.themes.find(
                            (tag) => tag.id === selectedTag,
                          )?.name ?? ""}
                        </span>
                      </>
                    )}
                    {selectedTag && search ? " en" : search ? " met" : ""}
                    {search && (
                      <>
                        {" de zoekterm "}
                        <span className={"font-bold"}>{search}</span>
                      </>
                    )}
                    . 😢
                  </P>

                  <Button
                    variant={"headerMain"}
                    className="forum-act mb-[16px] px-[5px] text-[18px] font-[400] text-white"
                    onClick={() => {
                      setSelectedTag("");
                      handleSearch({
                        target: { value: "" },
                      } as ChangeEvent<HTMLInputElement>);
                      setCurrentPage(1);
                    }}
                  >
                    Terug naar alle vragen
                  </Button>
                </Grid>
              )}
              {!isFetching && result.items && result.items.length > 0 && (
                <>
                  {result?.items?.map((item) => (
                    <ForumPostWrapper
                      item={mapToForumPost(item as ForumOverview)}
                      key={item.id}
                      isForAdults={audienceName == AudienceName.Adults}
                    />
                  ))}
                </>
              )}
            </>
          </Grid>
        </Container>
        <Pagination
          total={Math.ceil(result?.totalCount / FORUM_POST_PER_PAGE)}
          truncated
          onChange={changePage}
          isFetching={isFetching}
        />
      </main>
    </PageWrapper>
  );
}
